import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import EditOutlined from '@mui/icons-material/EditOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import { signOut } from 'firebase/auth';
import defaultProfilePic from '../assets/default.jpg';
import Navbar from './Navbar';

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0f2847 0%, #1e3a5f 100%)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8rem 2rem 4rem",
  },
  profileContainer: {
    backgroundColor: "rgba(59, 130, 246, 0.1)",
    backdropFilter: "blur(8px)",
    borderRadius: "1.2rem",
    padding: "3rem",
    border: "1px solid rgba(59, 130, 246, 0.2)",
    maxWidth: "600px",
    width: "100%",
  },
  profileHeader: {
    textAlign: "center",
    marginBottom: "2rem",
  },
  imageContainer: {
    position: "relative",
    width: "150px",
    height: "150px",
    margin: "0 auto 1.5rem",
  },
  profileImage: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    objectFit: "cover",
    border: "3px solid #3b82f6",
  },
  uploadButton: {
    position: "absolute",
    bottom: "0",
    right: "0",
    backgroundColor: "#3b82f6",
    color: "white",
    borderRadius: "50%",
    padding: "0.5rem",
    cursor: "pointer",
    border: "2px solid white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  inputGroup: {
    marginBottom: "1.5rem",
  },
  label: {
    display: "block",
    marginBottom: "0.5rem",
    color: "#d1d5db",
    fontSize: "0.9rem",
  },
  input: {
    width: "100%",
    padding: "0.8rem 1rem",
    borderRadius: "0.5rem",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(59, 130, 246, 0.2)",
    color: "white",
    fontSize: "1rem",
    transition: "all 0.2s ease",
    '&:focus': {
      outline: "none",
      borderColor: "#3b82f6",
      backgroundColor: "rgba(255, 255, 255, 0.15)",
    },
  },
  button: {
    backgroundColor: "#3b82f6",
    color: "white",
    padding: "0.8rem 1.5rem",
    borderRadius: "0.8rem",
    border: "none",
    cursor: "pointer",
    fontSize: "1rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    transition: "all 0.2s ease",
    '&:hover': {
      backgroundColor: "#2563eb",
    },
  },
  fileInput: {
    display: "none",
  },
  buttonContainer: {
    display: "flex",
    gap: "1rem",
    marginTop: "1.5rem",
  },
  signOutButton: {
    backgroundColor: "#ef4444",
    color: "white",
    padding: "0.8rem 1.5rem",
    borderRadius: "0.8rem",
    border: "none",
    cursor: "pointer",
    fontSize: "1rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    transition: "all 0.2s ease",
    '&:hover': {
      backgroundColor: "#dc2626",
    },
  },
};

const Profile = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    profileImage: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const userDoc = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userDoc);
        
        if (userSnap.exists()) {
          setUserData({
            name: userSnap.data().name || user.displayName || '',
            email: userSnap.data().email || user.email || '',
            profileImage: userSnap.data().profileImage || defaultProfilePic,
          });
        } else {
          // Create initial user data if it doesn't exist
          const initialUserData = {
            name: user.displayName || '',
            email: user.email || '',
            profileImage: defaultProfilePic,
            uid: user.uid,
          };
          await setDoc(userDoc, initialUserData);
          setUserData(initialUserData);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Set default data from auth user object
        setUserData({
          name: user.displayName || '',
          email: user.email || '',
          profileImage: defaultProfilePic,
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const user = auth.currentUser;
      const storageRef = ref(storage, `profileImages/${user.uid}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'users', user.uid), {
        profileImage: downloadURL,
      });

      setUserData(prev => ({
        ...prev,
        profileImage: downloadURL,
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const handleSave = async () => {
    try {
      const user = auth.currentUser;
      await updateDoc(doc(db, 'users', user.uid), {
        name: userData.name,
        email: userData.email,
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar currentPage="profile" />
        <div style={styles.page}>Loading...</div>
      </>
    );
  }

  return (
    <>
      <Navbar currentPage="profile" />
      <div style={styles.page}>
        <div style={styles.profileContainer}>
          <div style={styles.profileHeader}>
            <div style={styles.imageContainer}>
              <img
                src={userData.profileImage}
                alt="Profile"
                style={styles.profileImage}
              />
              <label htmlFor="imageUpload" style={styles.uploadButton}>
                <CloudUploadOutlined />
              </label>
              <input
                type="file"
                id="imageUpload"
                accept="image/*"
                onChange={handleImageUpload}
                style={styles.fileInput}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Name</label>
            <input
              type="text"
              value={userData.name}
              onChange={(e) => setUserData(prev => ({ ...prev, name: e.target.value }))}
              disabled={!isEditing}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={userData.email}
              onChange={(e) => setUserData(prev => ({ ...prev, email: e.target.value }))}
              disabled={!isEditing}
              style={styles.input}
            />
          </div>

          <div style={styles.buttonContainer}>
            <button
              onClick={isEditing ? handleSave : () => setIsEditing(true)}
              style={styles.button}
            >
              {isEditing ? (
                <>
                  <SaveOutlined /> Save Changes
                </>
              ) : (
                <>
                  <EditOutlined /> Edit Profile
                </>
              )}
            </button>
            <button
              onClick={handleSignOut}
              style={styles.signOutButton}
            >
              <LogoutOutlined /> Sign Out
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile; 